import { FC, useEffect, useState } from "react"
import { Button, Form, Input, Modal, Radio, Select } from "antd"
import { FormElement, SubmitCancelButtons, RaffleSelector, DeleteIconButton } from "components"
import { observer } from "mobx-react"
import { notifications } from "notifications"
import { ITask, ITaskReward } from "../api/types"
import { createTask, createTaskReward, deleteTaskReward, updateTask, updateTaskReward } from "../api"
import { COUNTRY_CODES } from "utils/countryCodes"
import { gameNamesDesktop } from "abstract/constants/game-ids"

const { Option } = Select

export const CreateTask: FC<{
  onClose: (task?: ITask) => void,
  initial?: ITask, advertiser: string,
  advertiserRegions: string[],
  buffType?: boolean,
}> = observer(({ onClose, initial: _initial, advertiser, advertiserRegions, buffType }) => {
  const [initial, setInitial] = useState<ITask | undefined>(_initial)
  const [rewards, setRewards] = useState<ITask["rewards"]>(initial?.rewards || [])
  const [submitting, setSubmitting] = useState(false)
  const [addRegion, setAddRegion] = useState(false)
  const [buffTaskType, setBuffTaskType] = useState("MATCH")
  const [form] = Form.useForm()

  useEffect(() => {
    const activeRewards = (_initial?.rewards || []).filter(r => r.isActive !== false)
    const missingRegions = advertiserRegions.filter(r => !activeRewards.find(reward => reward.geoRegion === r))
    setRewards([...activeRewards, ...missingRegions.map(r => ({ geoRegion: r, amount: 0, revenue: 0 }))])
    setInitial(_initial)
    setBuffTaskType(
      (_initial?.buffTask?.raffleIds && _initial.buffTask.raffleIds.length > 0) ? "RAFFLE" : "MATCH",
    )
  }, [_initial])

  const onFinishHandler = async(e: ITask & {rewards: Record<string, ITaskReward>}) => {
    const isEdit = !!initial?.id
    if (submitting) return
    if (!e.name) {
      return notifications.error("Please enter the task name")
    }
    if (!e.description) {
      return notifications.error("Please enter the task description")
    }
    if (Object.values(e.rewards).some(r => !r.amount)) {
      return notifications.error("Please enter the task reward")
    }
    if (Object.values(e.rewards).some(r => !r.revenue)) {
      return notifications.error("Please enter the task revenue")
    }

    if (e.buffTask?.goal !== undefined) {
      e.buffTask.goal = Number(e.buffTask?.goal)
    }
    if (buffType && (!e.buffTask?.goal || e.buffTask?.goal === 0)) {
      return notifications.error("Please enter the buff task goal")
    }

    if (buffType && buffTaskType === "MATCH" && !e.buffTask?.gameIds?.length) {
      e.buffTask!.gameIds = []
    }

    if (buffType && buffTaskType === "RAFFLE" && !e.buffTask?.raffleIds?.length) {
      e.buffTask!.raffleIds = []
    }

    if (buffType && buffTaskType === "MATCH" && e.buffTask?.gameIds?.length) {
      e.buffTask.gameIds = e.buffTask.gameIds.map(Number)
    }

    setSubmitting(true)

    const rewardsByGeoRegion = e.rewards

    const data = {
      ...initial,
      ...e,
    } as ITask

    if (typeof data.priority === "string") {
      data.priority = Number(data.priority)
    }

    data.rewards = Object.keys(rewardsByGeoRegion).map(r => ({
      ...rewardsByGeoRegion[r],
      amount: Number(rewardsByGeoRegion[r].amount),
      revenue: Number(rewardsByGeoRegion[r].revenue),
    }))

    if (isEdit) {
      e.id = initial?.id
      const updatedRewards: ITaskReward[] = []

      return updateTask(advertiser, data).then(async updated => {
        for (const r of data.rewards) {
          updatedRewards.push(r.id ? await updateTaskReward(r) : await createTaskReward(data.id as string, r))
        }
        updated.rewards = updatedRewards
        notifications.success("Task updated")
        close(updated)
      }).catch(e => {
        notifications.error(e?.message || e?.cause?.message || "Request failed")
        const written = updatedRewards.filter(Boolean)
        written.forEach(u => form.setFieldValue(["rewards", u.geoRegion, "id"], u.id))
        setRewards(prev => prev.map(row => written.find(u => u.geoRegion === row.geoRegion) ?? row))
      }).finally(() => {
        setSubmitting(false)
      })
    }

    createTask(advertiser, { ...data, active: false }).then(async created => {
      notifications.success("Task created")
      close(created)
    }).catch(e => {
      notifications.error(e?.message || e?.cause?.message || "Request failed")
    }).finally(() => {
      setSubmitting(false)
    })
  }
  const onFinishFailedHandler = (e: any) => {
    console.log(e)
  }

  const close = (task?: ITask) => {
    (document.getElementById("createTask") as HTMLFormElement).reset()
    onClose(task)
  }

  const onCreateReward = (geoRegion: string) => {
    setRewards([...rewards, { amount: 0, geoRegion, revenue: 0 }])
  }

  const onDeleteReward = (id: string) => {
    if (submitting) return
    setSubmitting(true)
    deleteTaskReward(id).then(() => {
      notifications.success("Reward deleted")
      setRewards(rewards.filter(r => r.id !== id))
    }).catch(e => {
      notifications.error(e?.message || e?.cause?.message || "Request failed")
    }).finally(() => {
      setSubmitting(false)
    })
  }

  useEffect(() => {
    (document.getElementById("createTask") as HTMLFormElement).reset()
  }, [initial])

  return (
    <Form onFinish={onFinishHandler} onFinishFailed={onFinishFailedHandler} form={form} id="createTask">
      <Modal
        open={addRegion}
        title="Select a Region"
        width="256px"
        footer={null}
        onCancel={() => setAddRegion(false)}
        style={{ top: "50%", transform: "translate(0, -50%)" }}
      >
        <Select
          allowClear
          mode="tags"
          value={[]}
          style={{ width: "100%" }}
          options={COUNTRY_CODES.map(({ code, name }) => ({ label: `${name} (${code})`, value: code }))}
          onSelect={r => {
            if (rewards.find(reward => reward.geoRegion === r)) return notifications.error("Region already present")
            r && onCreateReward(r)
            setAddRegion(false)
          }}
        />
      </Modal>
      <Form.Item
        label="Name"
        name="name"
        initialValue={initial?.name || ""}
        style={{ width: "100%" }}
        required
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Description"
        name="description"
        initialValue={initial?.description || ""}
        style={{ width: "100%" }}
        required
      >
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        label="Priority"
        name="priority"
        initialValue={initial?.priority || 0}
        style={{ width: "100%" }}
      >
        <Input type="number" />
      </Form.Item>
      <div
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
      >
        <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <h4>Rewards</h4>
          <Button
            type="primary"
            onClick={() => {
              setAddRegion(true)
            }}
          >
            Add Region
          </Button>
        </div>
        {rewards.map(r => (
          <div
            key={`${r.geoRegion}:${r.id ?? "new"}`}
            style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center" }}
          >
            <Form.Item
              name={["rewards", r.geoRegion, "id"]}
              initialValue={r?.id || ""}
              style={{ display: "none" }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={["rewards", r.geoRegion, "geoRegion"]}
              initialValue={r?.geoRegion || ""}
              style={{ display: "none" }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={`Reward for ${r.geoRegion}`}
              name={["rewards", r.geoRegion, "amount"]}
              initialValue={r?.amount || ""}
              style={{ width: "100%" }}
              required
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              label={`Revenue for ${r.geoRegion}`}
              name={["rewards", r.geoRegion, "revenue"]}
              initialValue={r?.revenue || ""}
              style={{ width: "100%", paddingLeft: 16 }}
              required
            >
              <Input type="number" />
            </Form.Item>
            <div style={{ position: "relative", width: 48, marginLeft: 4, top: -12 }}>
              {
                !advertiserRegions.includes(r.geoRegion)
                  ? (
                    <DeleteIconButton
                      onClick={() => {
                        if (r?.id) {
                          onDeleteReward(r.id as string)
                        } else {
                          setRewards(rewards.filter(reward => reward.geoRegion !== r.geoRegion))
                        }
                      }}
                    />
                  ) : null
              }
            </div>
          </div>
        ))}
        {buffType
        && (
          <>
            <div style={{ width: "100%", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <h4>Buff Task Type</h4>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                options={[{ label: "Match completion", value: "MATCH" }, { label: "Raffles participation", value: "RAFFLE" }]}
                value={buffTaskType}
                onChange={e => setBuffTaskType(e.target.value)}
              />
            </div>
            <Form.Item
              label="Goal"
              name={["buffTask", "goal"]}
              initialValue={initial?.buffTask?.goal || ""}
              style={{ width: "100%", paddingLeft: 16 }}
              required
            >
              <Input type="number" />
            </Form.Item>
            {buffTaskType === "MATCH" && (
              <FormElement type="select" label="Games" name={["buffTask", "gameIds"]} initialValue={initial?.buffTask?.gameIds}>
                <Select
                  mode="multiple"
                  placeholder="Please select games"
                  // onChange={e => setSelectedGames(e)}
                >
                  {Object.entries(gameNamesDesktop).map(item => {
                    return (
                      <Option value={item[0]} key={item[0]}>
                        {item[1]}
                      </Option>
                    )
                  })}
                </Select>
              </FormElement>
            )}
            {buffTaskType === "RAFFLE" && (
              <Form.Item label="Raffles" name={["buffTask", "raffleIds"]}>
                <RaffleSelector
                  mode="tags"
                  placeholder="Search and select raffles"
                  value={initial?.buffTask?.raffleIds?.join(",")}
                  onChange={value => {
                    const raffleIds = value ? value.split(",").filter(id => id.trim()) : []
                    form.setFieldValue(["buffTask", "raffleIds"], raffleIds)
                  }}
                />
              </Form.Item>
            )}
          </>
        )}
      </div>
      <SubmitCancelButtons onClose={() => close()} isLoading={submitting} />
    </Form>
  )
})
